import React, { useState, useCallback } from 'react';
import { Input } from 'antd';
import { css, cx } from '@emotion/css';

import { TextAreaProps } from 'antd/es/input';

export interface HintTextareaProps extends TextAreaProps {
  limit?: number;
  value?: string;
}

const { TextArea } = Input;

const styles = {
  textarea: css`
    display: inline-block;
    width: 100%;
    border: 1px solid #d9d9d9;
    border-radius: 2px;
    line-height: 1.5;
    transition: all 0.3s;
    box-shadow: none !important;
    .ant-form-item-has-error &,
    .ant-form-item-has-error &:hover {
      border-color: #f5222d;
    }
    &:hover {
      border-color: #40a9ff;
    }
  `,
  active: css`
    border-color: #40a9ff;
    outline: 0;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    .ant-form-item-has-error & {
      border-color: #ff4d4f;
      box-shadow: 0 0 0 2px rgba(245, 34, 45, 0.2);
    }
  `,
  activeOverflowed: css`
    border-color: #ff4d4f;
    box-shadow: 0 0 0 2px rgba(245, 34, 45, 0.2);
  `,
  disabled: css`
    background: #f5f5f5;
    &:hover {
      background: #d9d9d9;
    }
  `,
  input: css`
    &.ant-input {
      margin-bottom: 0;
      border: none;
      resize: none;
      font-size: inherit;
      &:focus {
        box-shadow: none;
      }
      &::-webkit-scrollbar {
        width: 4px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 2px;
        background: #d8d8d8;
      }
    }
  `,
  hintArea: css`
    padding: 4px 11px;
    text-align: right;
  `,
  hint: css`
    color: rgba(0, 0, 0, 0.45);
  `,
  overflowedHint: css`
    color: #f5222d;
  `,
  overflowed: css`
    &,
    &:hover {
      border-color: #f5222d;
    }
  `,
};

function HintTextarea(props: HintTextareaProps) {
  const {
    disabled,
    limit,
    value = '',
    onFocus,
    onBlur,
    className: classNameProp,
    style,
    ...otherProps
  } = props;

  const [active, setActive] = useState(false);
  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setActive(true);

      if (onFocus) {
        onFocus(event);
      }
    },
    [onFocus],
  );
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setActive(false);

      if (onBlur) {
        onBlur(event);
      }
    },
    [onBlur],
  );

  const isOverflow = limit !== undefined && value.length > limit;

  const hint = limit !== undefined && (
    <span className={cx(styles.hint, isOverflow && styles.overflowedHint)}>
      {`${value.length} / ${limit}`}
    </span>
  );

  const className = cx(
    classNameProp,
    styles.textarea,
    active && styles.active,
    isOverflow && styles.overflowed,
    disabled && styles.disabled,
    active && isOverflow && styles.activeOverflowed,
  );

  return (
    <div className={className} style={style}>
      <TextArea
        {...otherProps}
        className={styles.input}
        disabled={disabled}
        value={value}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {hint && <div className={styles.hintArea}>{hint}</div>}
    </div>
  );
}

export default HintTextarea;
